/**
 * Country Mode Switch Handler
 * Manages switching between FDI count and capital percentage display modes
 */

import { formatCountNumber, formatCapital } from '../features/fdi-map/ui/modal/modal-render.js';

class CountryModeSwitch {
    constructor() {
        this.currentMode = 'count'; // 'count' or 'capital'
        this.countData = new Map(); // term_id => count
        this.capitalData = new Map(); // term_id => capital_total
        this.countryNameToId = new Map(); // country_name => term_id
        this.totalCount = 0;
        this.totalCapital = 0;
        
        this.init();
    }
    
    init() {
        this.container = document.getElementById('country-mode-switch');
        if (!this.container) {
            console.warn('CountryModeSwitch: Container not found');
            return;
        }
        
        this.buttons = this.container.querySelectorAll('.switch-btn');
        this.loadingEl = this.container.querySelector('.switch-loading');
        this.errorEl = this.container.querySelector('.switch-error');
        
        this.bindEvents();
        this.loadInitialData();
    }
    
    bindEvents() {
        this.buttons.forEach(btn => {
            btn.addEventListener('click', (e) => this.handleSwitch(e));
            btn.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.handleSwitch(e);
                }
            });
        });
        
        // Listen for FacetWP updates
        document.addEventListener('facetwp-loaded', () => {
            this.refreshData();
        });
    }
    
    loadInitialData() {
        try {
            const stats = this.getLatestStats();
            if (!stats) {
                console.warn('CountryModeSwitch: No preloaded stats found on initial load');
                return;
            }

            this.applyStats(stats);
            this.updateUI();
        } catch (error) {
            console.error('CountryModeSwitch: Failed to load initial data', error);
            this.showError('Không thể tải dữ liệu ban đầu');
        }
    }

    getLatestStats() {
        const facetStats = window.FWP?.settings?.iphub_country_stats;
        if (facetStats && typeof facetStats === 'object') {
            return facetStats;
        }

        const preloadedStats = window.iphub_country_stats;
        if (preloadedStats && typeof preloadedStats === 'object') {
            return preloadedStats;
        }

        return null;
    }

    applyStats(stats) {
        this.countData.clear();
        this.capitalData.clear();
        this.countryNameToId.clear();

        if (stats?.counts) {
            Object.entries(stats.counts).forEach(([termId, count]) => {
                const parsedId = parseInt(termId, 10);
                if (!Number.isNaN(parsedId)) {
                    this.countData.set(parsedId, Number(count) || 0);
                }
            });
        }

        if (stats?.capital_totals) {
            Object.entries(stats.capital_totals).forEach(([termId, total]) => {
                const parsedId = parseInt(termId, 10);
                if (!Number.isNaN(parsedId)) {
                    this.capitalData.set(parsedId, Number(total) || 0);
                }
            });
        }

        if (stats?.names) {
            Object.entries(stats.names).forEach(([termId, name]) => {
                const parsedId = parseInt(termId, 10);
                if (!Number.isNaN(parsedId) && typeof name === 'string') {
                    this.countryNameToId.set(name.trim(), parsedId);
                }
            });
            console.log('CountryModeSwitch: Updated country name mapping:', this.countryNameToId);
        }

        this.totalCount = Number(stats?.total_count ?? this.sumMap(this.countData));
        this.totalCapital = Number(stats?.total_capital ?? this.sumMap(this.capitalData));
    }

    sumMap(map) {
        let total = 0;
        map.forEach((value) => {
            total += Number(value) || 0;
        });
        return total;
    }
    
    async handleSwitch(event) {
        const button = event.currentTarget;
        const newMode = button.dataset.mode;
        
        if (newMode === this.currentMode) {
            return;
        }
        
        try {
            // Update current mode
            this.currentMode = newMode;
            
            // Update button states
            this.updateButtonStates();
            
            // Update percentages
            this.updatePercentages();

            // Resort countries based on the active mode
            this.sortCountriesByCurrentMode();

            // Dispatch custom event
            this.dispatchModeChangeEvent();
            
        } catch (error) {
            console.error('CountryModeSwitch: Failed to switch mode', error);
            this.showError('Không thể chuyển đổi chế độ hiển thị');
        }
    }
    
    updateButtonStates() {
        this.buttons.forEach(btn => {
            const isActive = btn.dataset.mode === this.currentMode;
            btn.classList.toggle('active', isActive);
            btn.setAttribute('aria-pressed', isActive.toString());
        });
    }
    
    updatePercentages() {
        const data = this.currentMode === 'count' ? this.countData : this.capitalData;
        const total = this.currentMode === 'count' ? this.totalCount : this.totalCapital;
        
        // Update button labels with totals
        this.buttons.forEach(btn => {
            const mode = btn.dataset.mode;
            const btnTotal = mode === 'count' ? this.totalCount : this.totalCapital;
            const percentageEl = btn.querySelector('.btn-percentage');
            
            if (percentageEl && btnTotal > 0) {
                if (mode === 'capital') {
                    // Format capital as currency
                    percentageEl.textContent = this.formatCurrency(btnTotal);
                } else {
                    percentageEl.textContent = `${formatCountNumber(btnTotal)} dự án`;
                }
            } else if (percentageEl) {
                percentageEl.textContent = '';
            }
        });
        
        // Update FacetWP counters
        this.updateFacetCounters(data, total);
        this.dispatchStatsUpdatedEvent();
    }
    
    updateFacetCounters(data, total) {
        console.log('CountryModeSwitch: Updating facet counters with data:', data, 'total:', total);
        
        const countryFacet = document.querySelector('.facetwp-facet-country, .facetwp-facet[data-name="country"]');
        if (!countryFacet) {
            console.warn('CountryModeSwitch: Country facet not found');
            return;
        }
        
        console.log('CountryModeSwitch: Found country facet:', countryFacet);
        
        const checkboxes = countryFacet.querySelectorAll('.facetwp-checkbox');
        console.log('CountryModeSwitch: Found checkboxes:', checkboxes.length);
        
        checkboxes.forEach((checkbox, index) => {
            console.log(`CountryModeSwitch: Processing checkbox ${index}:`, checkbox);
            
            const termId = this.extractTermId(checkbox);
            if (!termId) {
                console.warn(`CountryModeSwitch: No term ID found for checkbox ${index}`);
                return;
            }
            
            const value = data.get(termId) || 0;
            const percentage = this.formatPercentage(value, total);
            
            console.log(`CountryModeSwitch: Term ID ${termId}, value: ${value}, percentage: ${percentage}%`);
            
            // Update counter display
            const counterEl = checkbox.querySelector('.iphub-percent-counter, .facetwp-counter');
            if (counterEl) {
                console.log(`CountryModeSwitch: Found counter element:`, counterEl);
                if (this.currentMode === 'capital') {
                    counterEl.textContent = `${percentage}% (${formatCapital(value)})`;
                    counterEl.title = `Vốn đăng ký: ${formatCapital(value)}`;
                } else {
                    counterEl.textContent = `${percentage}% (${formatCountNumber(value)})`;
                    counterEl.title = `Số dự án: ${formatCountNumber(value)}`;
                }
                console.log(`CountryModeSwitch: Updated counter text to: ${counterEl.textContent}`);
            } else {
                console.warn(`CountryModeSwitch: No counter element found for checkbox ${index}`);
            }
        });
    }
    
    extractTermId(checkbox) {
        console.log('CountryModeSwitch: Extracting term ID from checkbox:', checkbox);
        console.log('CountryModeSwitch: Checkbox HTML:', checkbox.outerHTML);
        
        // Try to extract term_id from various possible attributes
        const link = checkbox.querySelector('a');
        if (link) {
            const href = link.getAttribute('href');
            console.log('CountryModeSwitch: Found link href:', href);
            const match = href?.match(/[?&]country=([^&]+)/);
            if (match) {
                console.log('CountryModeSwitch: Found country in URL:', match[1]);
                // This might be a slug, we'd need to resolve to term_id
                // For now, we'll use a different approach
            }
        }
        
        // Try data attributes
        let termId = parseInt(checkbox.dataset.termId || checkbox.dataset.value || 0) || null;
        console.log('CountryModeSwitch: Term ID from data attributes:', termId);
        
        // If not found, try to extract from the checkbox input value
        if (!termId) {
            const input = checkbox.querySelector('input[type="checkbox"]');
            if (input) {
                termId = parseInt(input.value || 0) || null;
                console.log('CountryModeSwitch: Term ID from input value:', termId);
                console.log('CountryModeSwitch: Input element:', input);
            }
        }
        
        // If still not found, try to extract from the label text or other attributes
        if (!termId) {
            const label = checkbox.querySelector('label');
            if (label) {
                // Try to find term_id in data attributes of the label
                termId = parseInt(label.dataset.termId || label.dataset.value || 0) || null;
                console.log('CountryModeSwitch: Term ID from label:', termId);
                console.log('CountryModeSwitch: Label element:', label);
            }
        }
        
        // If still not found, try to get from the checkbox's parent or other elements
        if (!termId) {
            // Try to find any element with data-term-id
            const termElement = checkbox.querySelector('[data-term-id]') || checkbox.closest('[data-term-id]');
            if (termElement) {
                termId = parseInt(termElement.dataset.termId || 0) || null;
                console.log('CountryModeSwitch: Term ID from closest element:', termId);
            }
        }
        
        // If still not found, try to extract from the checkbox's text content or other methods
        if (!termId) {
            // Try to get from the checkbox's text content (country name)
            const textContent = checkbox.textContent?.trim();
            console.log('CountryModeSwitch: Checkbox text content:', textContent);
            
            // Try to find term by name in our mapping
        if (textContent && this.countryNameToId.size > 0) {
                // Remove flag image and other HTML content, get clean country name
                const cleanName = textContent.replace(/🇦🇪|🇦🇫|🇦🇬|🇦🇮|🇦🇱|🇦🇲|🇦🇴|🇦🇶|🇦🇷|🇦🇸|🇦🇹|🇦🇺|🇦🇼|🇦🇽|🇦🇿|🇧🇦|🇧🇧|🇧🇩|🇧🇪|🇧🇫|🇧🇬|🇧🇭|🇧🇮|🇧🇯|🇧🇱|🇧🇲|🇧🇳|🇧🇴|🇧🇶|🇧🇷|🇧🇸|🇧🇹|🇧🇻|🇧🇼|🇧🇾|🇧🇿|🇨🇦|🇨🇨|🇨🇩|🇨🇫|🇨🇬|🇨🇭|🇨🇮|🇨🇰|🇨🇱|🇨🇲|🇨🇳|🇨🇴|🇨🇵|🇨🇷|🇨🇺|🇨🇻|🇨🇼|🇨🇽|🇨🇾|🇨🇿|🇩🇪|🇩🇬|🇩🇯|🇩🇰|🇩🇲|🇩🇴|🇩🇿|🇪🇦|🇪🇨|🇪🇪|🇪🇬|🇪🇭|🇪🇷|🇪🇸|🇪🇹|🇪🇺|🇫🇮|🇫🇯|🇫🇰|🇫🇲|🇫🇴|🇫🇷|🇬🇦|🇬🇧|🇬🇩|🇬🇪|🇬🇫|🇬🇬|🇬🇭|🇬🇮|🇬🇱|🇬🇲|🇬🇳|🇬🇵|🇬🇶|🇬🇷|🇬🇸|🇬🇹|🇬🇺|🇬🇼|🇬🇾|🇭🇰|🇭🇲|🇭🇳|🇭🇷|🇭🇹|🇭🇺|🇮🇨|🇮🇩|🇮🇪|🇮🇱|🇮🇲|🇮🇳|🇮🇴|🇮🇶|🇮🇷|🇮🇸|🇮🇹|🇯🇪|🇯🇲|🇯🇴|🇯🇵|🇰🇪|🇰🇬|🇰🇭|🇰🇮|🇰🇲|🇰🇳|🇰🇵|🇰🇷|🇰🇼|🇰🇾|🇰🇿|🇱🇦|🇱🇧|🇱🇨|🇱🇮|🇱🇰|🇱🇷|🇱🇸|🇱🇹|🇱🇺|🇱🇻|🇱🇾|🇲🇦|🇲🇨|🇲🇩|🇲🇪|🇲🇫|🇲🇬|🇲🇭|🇲🇰|🇲🇱|🇲🇲|🇲🇳|🇲🇴|🇲🇵|🇲🇶|🇲🇷|🇲🇸|🇲🇹|🇲🇺|🇲🇻|🇲🇼|🇲🇽|🇲🇾|🇲🇿|🇳🇦|🇳🇨|🇳🇪|🇳🇫|🇳🇬|🇳🇮|🇳🇱|🇳🇴|🇳🇵|🇳🇷|🇳🇺|🇳🇿|🇴🇲|🇵🇦|🇵🇪|🇵🇫|🇵🇬|🇵🇭|🇵🇰|🇵🇱|🇵🇲|🇵🇳|🇵🇷|🇵🇸|🇵🇹|🇵🇼|🇵🇾|🇶🇦|🇷🇪|🇷🇴|🇷🇸|🇷🇺|🇷🇼|🇸🇦|🇸🇧|🇸🇨|🇸🇩|🇸🇪|🇸🇬|🇸🇭|🇸🇮|🇸🇯|🇸🇰|🇸🇱|🇸🇲|🇸🇳|🇸🇴|🇸🇷|🇸🇸|🇸🇹|🇸🇻|🇸🇽|🇸🇾|🇸🇿|🇹🇦|🇹🇨|🇹🇩|🇹🇫|🇹🇬|🇹🇭|🇹🇯|🇹🇰|🇹🇱|🇹🇲|🇹🇳|🇹🇴|🇹🇷|🇹🇹|🇹🇻|🇹🇼|🇹🇿|🇺🇦|🇺🇬|🇺🇲|🇺🇳|🇺🇸|🇺🇾|🇺🇿|🇻🇦|🇻🇨|🇻🇪|🇻🇬|🇻🇮|🇻🇳|🇻🇺|🇼🇫|🇼🇸|🇾🇪|🇾🇹|🇿🇦|🇿🇲|🇿🇼/g, '').trim();
                
                // Try exact match first
                termId = this.countryNameToId.get(cleanName);
                if (termId) {
                    console.log('CountryModeSwitch: Found term ID by exact name match:', cleanName, '->', termId);
                } else {
                    // Try partial match
                    for (const [name, id] of this.countryNameToId) {
                        if (name.includes(cleanName) || cleanName.includes(name)) {
                            termId = id;
                            console.log('CountryModeSwitch: Found term ID by partial name match:', cleanName, '->', name, '->', termId);
                            break;
                        }
                    }
                }
            }
        }
        
        console.log('CountryModeSwitch: Final term ID:', termId);
        return termId;
    }

    sortCountriesByCurrentMode() {
        const countryFacet = document.querySelector('.facetwp-facet-country, .facetwp-facet[data-name="country"]');
        if (!countryFacet) {
            console.warn('CountryModeSwitch: Cannot sort countries - facet not found');
            return;
        }

        const checkboxNodes = Array.from(countryFacet.querySelectorAll('.facetwp-checkbox'));
        if (checkboxNodes.length === 0) {
            return;
        }

        const parent = checkboxNodes[0].parentElement;
        if (!parent) {
            console.warn('CountryModeSwitch: Cannot sort countries - parent container missing');
            return;
        }

        const dataMap = this.getCurrentData();
        if (!(dataMap instanceof Map) || dataMap.size === 0) {
            // No data to sort with; keep existing order
            return;
        }

        const selectedClassNames = ['checked', 'selected', 'facetwp-checked'];
        const isSelected = (el) => selectedClassNames.some(className => el.classList.contains(className));

        const itemsWithMeta = checkboxNodes.map((item, index) => {
            const termId = this.extractTermId(item);
            const hasValue = typeof termId === 'number' && dataMap.has(termId);
            const rawValue = hasValue ? Number(dataMap.get(termId)) : Number.NaN;
            const value = Number.isFinite(rawValue) ? rawValue : (hasValue ? 0 : Number.NEGATIVE_INFINITY);
            return {
                item,
                index,
                selected: isSelected(item),
                value
            };
        });

        itemsWithMeta.sort((a, b) => {
            if (a.selected !== b.selected) {
                return a.selected ? -1 : 1;
            }

            if (a.value !== b.value) {
                return b.value - a.value;
            }

            return a.index - b.index;
        });

        // Preserve FacetWP overflow/toggle helpers at the bottom of the list
        const overflowAnchor = parent.querySelector('.facetwp-overflow, .facetwp-toggle');

        itemsWithMeta.forEach(({ item }) => {
            if (overflowAnchor) {
                parent.insertBefore(item, overflowAnchor);
            } else {
                parent.appendChild(item);
            }
        });
    }
    
    formatCurrency(amount) {
        return formatCapital(amount);
    }
    
    formatPercentage(count, total) {
        if (!total || total <= 0) return '0.00';
        const pct = (count / total) * 100;
        if (pct < 0.005) return '< 0.01';
        return (Math.round(pct * 100) / 100).toFixed(2);
    }
    
    refreshData() {
        try {
            const stats = this.getLatestStats();
            if (!stats) {
                console.warn('CountryModeSwitch: No stats available during refresh');
                return;
            }

            this.applyStats(stats);
            this.updateUI();
        } catch (error) {
            console.error('CountryModeSwitch: Failed to refresh data', error);
        }
    }
    
    updateUI() {
        this.updateButtonStates();
        this.updatePercentages();
        this.sortCountriesByCurrentMode();
    }
    
    showLoading() {
        if (this.loadingEl) {
            this.loadingEl.classList.remove('hidden');
            this.loadingEl.classList.add('flex');
        }
    }
    
    hideLoading() {
        if (this.loadingEl) {
            this.loadingEl.classList.add('hidden');
            this.loadingEl.classList.remove('flex');
        }
    }
    
    showError(message) {
        if (this.errorEl) {
            this.errorEl.querySelector('.error-message').textContent = message;
            this.errorEl.classList.remove('hidden');
        }
    }
    
    hideError() {
        if (this.errorEl) {
            this.errorEl.classList.add('hidden');
        }
    }
    
    dispatchModeChangeEvent() {
        const event = new CustomEvent('iphub:country-mode-changed', {
            detail: {
                mode: this.currentMode,
                countData: Object.fromEntries(this.countData),
                capitalData: Object.fromEntries(this.capitalData),
                totalCount: this.totalCount,
                totalCapital: this.totalCapital
            }
        });
        document.dispatchEvent(event);
    }

    dispatchStatsUpdatedEvent() {
        const event = new CustomEvent('iphub:country-stats-updated', {
            detail: {
                mode: this.currentMode,
                countData: Object.fromEntries(this.countData),
                capitalData: Object.fromEntries(this.capitalData),
                totalCount: this.totalCount,
                totalCapital: this.totalCapital
            }
        });
        document.dispatchEvent(event);
    }
    
    getCurrentMode() {
        return this.currentMode;
    }
    
    getCurrentData() {
        return this.currentMode === 'count' ? this.countData : this.capitalData;
    }
    
    getCurrentTotal() {
        return this.currentMode === 'count' ? this.totalCount : this.totalCapital;
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    window.iphubCountryModeSwitch = new CountryModeSwitch();
});

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
    module.exports = CountryModeSwitch;
}
